<nav mat-tab-nav-bar [tabPanel]="tabPanel">
  <a mat-tab-link
     *ngFor="let tab of tabs | visibleFinanceTabs"
     (click)="gotoTab(tab)"
     [active]="tab.link === activeTab.link">
    {{tab.title}}
  </a>
</nav>
<mat-tab-nav-panel #tabPanel></mat-tab-nav-panel>

<section class="connections-page">
  <h1>Accounting Connections</h1>
  <p class="intro">Manage Wallit’s single QuickBooks Online accounting connection.</p>

  <mat-card *ngIf="loading" class="connection-card state-row">
    <mat-spinner diameter="28"></mat-spinner>
    <span>Loading connection status…</span>
  </mat-card>

  <mat-card *ngIf="!loading" class="connection-card">
    <div class="connection-heading">
      <div>
        <h2>QuickBooks Online</h2>
        <span class="state-pill" [attr.data-state]="status.state">{{stateLabels[status.state]}}</span>
      </div>
      <mat-icon aria-hidden="true">account_balance</mat-icon>
    </div>

    <div *ngIf="status.state === 'CONFIGURATION_UNAVAILABLE'" class="message warning">
      QuickBooks connection management is temporarily unavailable. No other Finance feature is affected.
    </div>

    <div *ngIf="status.state === 'DISCONNECTED'" class="message">
      No QuickBooks company is connected to this Wallit environment.
    </div>

    <div *ngIf="status.state === 'REMOVED'" class="message">
      The previous QuickBooks connection was removed.
    </div>

    <div *ngIf="status.state === 'AUTHORIZATION_REQUIRES_REPLACEMENT'" class="message warning">
      Authorization is no longer usable. Remove this connection, then add it again. Company switching and reconnect are not available.
    </div>

    <div *ngIf="status.state === 'REFRESH_RECONCILIATION_REQUIRED'" class="message warning">
      QuickBooks token refresh could not be confirmed. Remove this connection, then add it again.
    </div>

    <div *ngIf="status.state === 'REVOCATION_UNKNOWN' || status.cleanupRequired" class="message warning">
      Intuit token revocation could not be confirmed. The retained encrypted credentials are quarantined and cannot be used for ordinary QuickBooks requests. Use explicit cleanup to retry once.
    </div>

    <dl *ngIf="status.company" class="company-details">
      <div>
        <dt>Company</dt>
        <dd>{{status.company.companyName || 'Not provided'}}</dd>
      </div>
      <div>
        <dt>Legal name</dt>
        <dd>{{status.company.legalName || 'Not provided'}}</dd>
      </div>
      <div>
        <dt>Country</dt>
        <dd>{{status.company.country || 'Not provided'}}</dd>
      </div>
      <div>
        <dt>Verified</dt>
        <dd>{{status.company.verifiedAt | date:'medium'}}</dd>
      </div>
    </dl>

    <div class="actions">
      <button mat-raised-button color="primary"
              *ngIf="status.actions.canConnect"
              [disabled]="starting"
              (click)="connect()">
        <mat-icon>add_link</mat-icon>
        {{starting ? 'Starting connection…' : 'Connect QuickBooks'}}
      </button>

      <button mat-stroked-button color="warn"
              *ngIf="status.actions.canRemove"
              [disabled]="removing"
              (click)="remove()">
        <mat-icon>link_off</mat-icon>
        {{removing ? 'Removing…' : 'Remove connection'}}
      </button>

      <button mat-stroked-button
              *ngIf="status.actions.canRetryCleanup"
              [disabled]="cleaningUp"
              (click)="retryCleanup()">
        <mat-icon>cleaning_services</mat-icon>
        {{cleaningUp ? 'Retrying cleanup…' : 'Retry cleanup'}}
      </button>
    </div>
  </mat-card>

  <section *ngIf="wallitProjection || wallitError" class="comparison-section">
    <div class="comparison-heading">
      <button type="button"
              class="comparison-toggle"
              aria-controls="account-comparison-content account-qbo-comparison-content"
              [attr.aria-expanded]="isComparisonSectionExpanded('account')"
              (click)="toggleComparisonSection('account')">
        <span>
          <strong>Read-only Chart of Accounts comparison</strong>
          <small>Compare Wallit's active Finance accounts with QuickBooks. Matches are advisory only; no mapping or account changes are made.</small>
        </span>
        <mat-icon aria-hidden="true">{{isComparisonSectionExpanded('account') ? 'expand_less' : 'expand_more'}}</mat-icon>
      </button>
      <button mat-raised-button color="primary"
              *ngIf="!loading && status.state === 'CONNECTED_VERIFIED'"
              [hidden]="!isComparisonSectionExpanded('account')"
              [disabled]="catalogLoading"
              (click)="loadAccountCatalog()">
        <mat-icon>download</mat-icon>
        {{catalogLoading ? 'Loading complete catalog…' : 'Load QuickBooks accounts'}}
      </button>
    </div>

    <mat-card id="account-comparison-content"
              class="comparison-card wallit-card"
              [hidden]="!isComparisonSectionExpanded('account')">
      <div class="card-heading">
        <div>
          <h3>Wallit Chart of Accounts</h3>
          <p *ngIf="wallitProjection">All active non-root Finance accounts as of {{wallitProjection.projectionAsOfDate}}</p>
        </div>
        <span *ngIf="wallitProjection" class="count-pill">{{wallitProjection.accounts.length}}</span>
      </div>

      <div *ngIf="wallitLoading" class="state-row">
        <mat-spinner diameter="24"></mat-spinner>
        <span>Loading the Wallit Chart of Accounts…</span>
      </div>
      <div *ngIf="wallitError" class="message warning">
        The Wallit Chart of Accounts is temporarily unavailable. QuickBooks accounts were not requested.
      </div>
      <div *ngIf="!wallitLoading && wallitProjection && !wallitProjection.accounts.length" class="message">
        No active non-root Finance accounts are available for comparison.
      </div>

      <div *ngIf="wallitProjection?.accounts?.length" class="table-scroll">
        <table class="comparison-table">
          <thead>
            <tr>
              <th>Wallit account</th>
              <th>Class and parent</th>
              <th>Comparison scope</th>
              <th>Advisory hint</th>
            </tr>
          </thead>
          <ng-container *ngFor="let group of wallitAccountGroups">
            <tbody>
              <tr class="wallit-account-group-row">
                <th colspan="4">
                  <button type="button"
                          class="wallit-account-group-toggle"
                          [attr.aria-controls]="'wallit-account-group-' + group.accountTypeCode"
                          [attr.aria-expanded]="isWallitAccountGroupExpanded(group.accountTypeCode)"
                          (click)="toggleWallitAccountGroup(group.accountTypeCode)">
                    <span>
                      <strong>{{group.label}}</strong>
                      <small>{{group.accounts.length}} account{{group.accounts.length === 1 ? '' : 's'}}</small>
                    </span>
                    <mat-icon aria-hidden="true">
                      {{isWallitAccountGroupExpanded(group.accountTypeCode) ? 'expand_less' : 'expand_more'}}
                    </mat-icon>
                  </button>
                </th>
              </tr>
            </tbody>
            <tbody [id]="'wallit-account-group-' + group.accountTypeCode"
                   [hidden]="!isWallitAccountGroupExpanded(group.accountTypeCode)">
              <tr *ngFor="let account of group.accounts">
                <td>
                  <strong>{{account.name}}</strong>
                  <span>Code: {{account.code}}</span>
                  <small *ngIf="account.isSystem" class="system-label">System account</small>
                </td>
                <td>
                  <span>{{account.accountTypeCode | titlecase}}</span>
                  <small>Parent: {{account.parent ? (account.parent.code + ' — ' + account.parent.name) : 'None'}}</small>
                </td>
                <td>
                  <span *ngFor="let reason of account.eligibilityReasons" class="evidence-chip">
                    {{eligibilityLabels[reason]}}
                  </span>
                </td>
                <td>
                  <ng-container *ngIf="hintFor(account) as hint; else catalogNotLoaded">
                    <span class="hint-pill" [attr.data-hint]="hint.result">{{hintLabels[hint.result]}}</span>
                    <small *ngFor="let quickBooksAccount of quickBooksAccountsFor(hint)">
                      {{quickBooksAccount.fullyQualifiedName}}
                      ({{quickBooksAccount.active === true ? 'Active' : quickBooksAccount.active === false ? 'Inactive' : 'State unknown'}})
                    </small>
                    <small *ngIf="hint.sharedQuickBooksAccountIds.length" class="shared-label">
                      Same QuickBooks Account is hinted for multiple Wallit accounts.
                    </small>
                  </ng-container>
                  <ng-template #catalogNotLoaded><span class="muted">Load QuickBooks accounts to compare.</span></ng-template>
                </td>
              </tr>
            </tbody>
          </ng-container>
        </table>
      </div>

      <details *ngIf="wallitProjection?.diagnostics?.length" class="diagnostics">
        <summary>{{wallitProjection?.diagnostics?.length}} projection diagnostic(s)</summary>
        <ul>
          <li *ngFor="let diagnostic of wallitProjection?.diagnostics">
            <code>{{diagnostic.reasonCode}}</code>
            <span *ngIf="diagnostic.contractId">Contract {{diagnostic.contractId}}</span>
            <span *ngIf="diagnostic.metadataRole">Role {{diagnostic.metadataRole}}</span>
            <span *ngIf="diagnostic.accountId">Account {{diagnostic.accountId}}</span>
          </li>
        </ul>
      </details>
    </mat-card>

    <mat-card id="account-qbo-comparison-content"
              class="comparison-card qbo-card"
              [hidden]="!isComparisonSectionExpanded('account')">
      <div class="card-heading">
        <div>
          <h3>QuickBooks Accounts</h3>
          <p *ngIf="accountCatalog">Complete catalog retrieved {{accountCatalog.retrievedAt | date:'medium'}}</p>
        </div>
        <span *ngIf="accountCatalog" class="count-pill">{{accountCatalog.accounts.length}}</span>
      </div>

      <div *ngIf="!accountCatalog && !catalogLoading && !catalogError" class="message">
        QuickBooks has not been contacted. Select “Load QuickBooks accounts” to request the complete active and inactive catalog.
      </div>
      <div *ngIf="catalogLoading" class="state-row">
        <mat-spinner diameter="24"></mat-spinner>
        <span>Loading every active and inactive QuickBooks Account…</span>
      </div>
      <div *ngIf="catalogError" class="message warning">{{catalogError}}</div>
      <div *ngIf="accountCatalog && !accountCatalog.accounts.length" class="message">
        The complete QuickBooks Account catalog is empty.
      </div>

      <div *ngIf="accountCatalog?.accounts?.length" class="table-scroll">
        <table class="comparison-table qbo-table">
          <thead>
            <tr>
              <th>QuickBooks account</th>
              <th>State</th>
              <th>Classification</th>
              <th>Type / subtype</th>
              <th>Currency</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of quickBooksAccountRows">
              <td class="qbo-account-cell" [attr.aria-label]="row.account.fullyQualifiedName">
                <div class="qbo-account-label" [style.padding-left.px]="row.depth * 24">
                  <span *ngIf="row.depth > 0" class="qbo-hierarchy-marker" aria-hidden="true"></span>
                  <div>
                    <strong>{{row.account.name}}</strong>
                    <small>Account number: {{row.account.acctNum || 'Not provided'}}</small>
                  </div>
                </div>
              </td>
              <td>{{row.account.active === true ? 'Active' : row.account.active === false ? 'Inactive' : 'Unknown'}}</td>
              <td>{{row.account.classification}}</td>
              <td>{{row.account.accountType}} / {{row.account.accountSubType || 'Unknown'}}</td>
              <td>{{currencyDisplay(row.account)}}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </mat-card>
  </section>

  <section class="comparison-section party-comparison-section">
    <div class="comparison-heading">
      <button type="button"
              class="comparison-toggle"
              aria-controls="vendor-comparison-content"
              [attr.aria-expanded]="isComparisonSectionExpanded('vendor')"
              (click)="toggleComparisonSection('vendor')">
        <span>
          <strong>Read-only supplier / Vendor comparison</strong>
          <small>Wallit vendor rows and exact-name hints are provisional information only. No party is approved, mapped, or changed.</small>
        </span>
        <mat-icon aria-hidden="true">{{isComparisonSectionExpanded('vendor') ? 'expand_less' : 'expand_more'}}</mat-icon>
      </button>
      <button mat-raised-button color="primary"
              *ngIf="!loading && status.state === 'CONNECTED_VERIFIED'"
              [hidden]="!isComparisonSectionExpanded('vendor')"
              [disabled]="vendorCatalogLoading"
              (click)="loadVendorCatalog()">
        <mat-icon>visibility</mat-icon>
        {{vendorCatalogLoading ? 'Loading complete Vendor catalog…' : 'Load QuickBooks Vendors'}}
      </button>
    </div>

    <div id="vendor-comparison-content"
         class="party-card-grid"
         [hidden]="!isComparisonSectionExpanded('vendor')">
      <mat-card class="comparison-card wallit-card">
        <div class="card-heading">
          <div>
            <h3>Wallit supplier / carrier candidates</h3>
            <p>Source: the Wallit vendors representation. Operational keys are shown as stored; no lifecycle state is inferred.</p>
          </div>
          <span *ngIf="supplierCandidates" class="count-pill">{{supplierCandidates.suppliers.length}}</span>
        </div>

        <div *ngIf="supplierCandidatesLoading" class="state-row">
          <mat-spinner diameter="24"></mat-spinner>
          <span>Loading Wallit supplier candidates…</span>
        </div>
        <div *ngIf="supplierCandidatesError" class="message warning">
          Wallit supplier candidates are temporarily unavailable. QuickBooks Vendors were not requested.
        </div>
        <div *ngIf="!supplierCandidatesLoading && supplierCandidates && !supplierCandidates.suppliers.length" class="message">
          No Wallit supplier candidates are available.
        </div>

        <div *ngIf="supplierCandidates?.suppliers?.length" class="table-scroll">
          <table class="comparison-table party-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Source / type</th>
                <th>Advisory hint</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let candidate of supplierCandidates?.suppliers">
                <td>
                  <strong>{{candidate.displayName}}</strong>
                  <small>Operational key: {{candidate.operationalKey}}</small>
                  <small>Wallit source ID: {{candidate.sourceId}}</small>
                </td>
                <td><span class="source-pill">Wallit / Supplier candidate</span></td>
                <td>
                  <ng-container *ngIf="vendorHintFor(candidate) as hint; else vendorsNotLoaded">
                    <span class="hint-pill" [attr.data-hint]="hint.result">{{partyHintLabels[hint.result]}}</span>
                    <small *ngFor="let vendor of vendorsFor(hint)">
                      QuickBooks Vendor: {{vendor.displayName}} ({{vendor.active ? 'Active' : 'Inactive'}})
                    </small>
                    <small *ngIf="hint.duplicateWallitSourceIds.length > 1" class="shared-label">
                      {{hint.duplicateWallitSourceIds.length}} Wallit rows share this normalized name.
                    </small>
                    <small *ngIf="hint.duplicateQuickBooksEntityIds.length" class="shared-label">
                      Multiple QuickBooks Vendors share this normalized name; no result is selected.
                    </small>
                    <small *ngIf="hasPossibleRoleOverlap(candidate.displayName)" class="shared-label">
                      Possible role overlap: the same normalized name exists in both QuickBooks catalogs.
                    </small>
                  </ng-container>
                  <ng-template #vendorsNotLoaded><span class="muted">Load QuickBooks Vendors to compare.</span></ng-template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </mat-card>

      <mat-card class="comparison-card qbo-card">
        <div class="card-heading">
          <div>
            <h3>QuickBooks Vendors</h3>
            <p *ngIf="vendorCatalog">Complete catalog retrieved {{vendorCatalog.retrievedAt | date:'medium'}}</p>
          </div>
          <span *ngIf="vendorCatalog" class="count-pill">{{vendorCatalog.vendors.length}}</span>
        </div>
        <div *ngIf="!vendorCatalog && !vendorCatalogLoading && !vendorCatalogError" class="message">
          QuickBooks has not been contacted for Vendors. Use the separate Vendor load action above.
        </div>
        <div *ngIf="vendorCatalogLoading" class="state-row">
          <mat-spinner diameter="24"></mat-spinner>
          <span>Loading every active and inactive QuickBooks Vendor…</span>
        </div>
        <div *ngIf="vendorCatalogError" class="message warning">{{vendorCatalogError}}</div>
        <div *ngIf="vendorCatalog && !vendorCatalog.vendors.length" class="message">
          The complete QuickBooks Vendor catalog is empty.
        </div>
        <div *ngIf="vendorCatalog?.vendors?.length" class="table-scroll">
          <table class="comparison-table party-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Source / type</th>
                <th>State</th>
                <th>Last updated</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let vendor of vendorCatalog?.vendors">
                <td>
                  <strong>{{vendor.displayName}}</strong>
                  <small *ngIf="vendor.companyName">Company: {{vendor.companyName}}</small>
                </td>
                <td><span class="source-pill">QuickBooks Online / Vendor</span></td>
                <td><span class="state-pill compact" [attr.data-active]="vendor.active">{{vendor.active ? 'Active' : 'Inactive'}}</span></td>
                <td>{{vendor.lastUpdatedAt ? (vendor.lastUpdatedAt | date:'medium') : 'Not provided'}}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </mat-card>
    </div>
  </section>

  <section class="comparison-section party-comparison-section">
    <div class="comparison-heading">
      <button type="button"
              class="comparison-toggle"
              aria-controls="customer-comparison-content"
              [attr.aria-expanded]="isComparisonSectionExpanded('customer')"
              (click)="toggleComparisonSection('customer')">
        <span>
          <strong>Read-only employer / Customer comparison</strong>
          <small>Wallit company rows and exact-name hints are provisional information only. No party is approved, mapped, or changed.</small>
        </span>
        <mat-icon aria-hidden="true">{{isComparisonSectionExpanded('customer') ? 'expand_less' : 'expand_more'}}</mat-icon>
      </button>
      <button mat-raised-button color="primary"
              *ngIf="!loading && status.state === 'CONNECTED_VERIFIED'"
              [hidden]="!isComparisonSectionExpanded('customer')"
              [disabled]="customerCatalogLoading"
              (click)="loadCustomerCatalog()">
        <mat-icon>visibility</mat-icon>
        {{customerCatalogLoading ? 'Loading complete Customer catalog…' : 'Load QuickBooks Customers'}}
      </button>
    </div>

    <div id="customer-comparison-content"
         class="party-card-grid"
         [hidden]="!isComparisonSectionExpanded('customer')">
      <mat-card class="comparison-card wallit-card">
        <div class="card-heading">
          <div>
            <h3>Wallit employer / client candidates</h3>
            <p>Source: non-deleted Wallit teamcompanies rows. Private company details are not loaded.</p>
          </div>
          <span *ngIf="companyCandidates" class="count-pill">{{companyCandidates.companies.length}}</span>
        </div>
        <div *ngIf="companyCandidatesLoading" class="state-row">
          <mat-spinner diameter="24"></mat-spinner>
          <span>Loading Wallit company candidates…</span>
        </div>
        <div *ngIf="companyCandidatesError" class="message warning">
          Wallit company candidates are temporarily unavailable. QuickBooks Customers were not requested.
        </div>
        <div *ngIf="!companyCandidatesLoading && companyCandidates && !companyCandidates.companies.length" class="message">
          No Wallit company candidates are available.
        </div>
        <div *ngIf="companyCandidates?.companies?.length" class="table-scroll">
          <table class="comparison-table party-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Source / type</th>
                <th>Advisory hint</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let candidate of companyCandidates?.companies">
                <td>
                  <strong>{{candidate.displayName}}</strong>
                  <small>Classification: {{candidate.classification || 'Not provided'}}</small>
                  <small>Wallit source ID: {{candidate.sourceId}}</small>
                </td>
                <td><span class="source-pill">Wallit / Company candidate</span></td>
                <td>
                  <ng-container *ngIf="customerHintFor(candidate) as hint; else customersNotLoaded">
                    <span class="hint-pill" [attr.data-hint]="hint.result">{{partyHintLabels[hint.result]}}</span>
                    <small *ngFor="let customer of customersFor(hint)">
                      QuickBooks Customer: {{customer.displayName}} ({{customer.active ? 'Active' : 'Inactive'}})
                    </small>
                    <small *ngIf="hint.duplicateWallitSourceIds.length > 1" class="shared-label">
                      {{hint.duplicateWallitSourceIds.length}} Wallit rows share this normalized name.
                    </small>
                    <small *ngIf="hint.duplicateQuickBooksEntityIds.length" class="shared-label">
                      Multiple QuickBooks Customers share this normalized name; no result is selected.
                    </small>
                    <small *ngIf="hasPossibleRoleOverlap(candidate.displayName)" class="shared-label">
                      Possible role overlap: the same normalized name exists in both QuickBooks catalogs.
                    </small>
                  </ng-container>
                  <ng-template #customersNotLoaded><span class="muted">Load QuickBooks Customers to compare.</span></ng-template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </mat-card>

      <mat-card class="comparison-card qbo-card">
        <div class="card-heading">
          <div>
            <h3>QuickBooks Customers</h3>
            <p *ngIf="customerCatalog">Complete catalog retrieved {{customerCatalog.retrievedAt | date:'medium'}}</p>
          </div>
          <span *ngIf="customerCatalog" class="count-pill">{{customerCatalog.customers.length}}</span>
        </div>
        <div *ngIf="!customerCatalog && !customerCatalogLoading && !customerCatalogError" class="message">
          QuickBooks has not been contacted for Customers. Use the separate Customer load action above.
        </div>
        <div *ngIf="customerCatalogLoading" class="state-row">
          <mat-spinner diameter="24"></mat-spinner>
          <span>Loading every active and inactive QuickBooks Customer…</span>
        </div>
        <div *ngIf="customerCatalogError" class="message warning">{{customerCatalogError}}</div>
        <div *ngIf="customerCatalog && !customerCatalog.customers.length" class="message">
          The complete QuickBooks Customer catalog is empty.
        </div>
        <div *ngIf="customerCatalog?.customers?.length" class="table-scroll">
          <table class="comparison-table party-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Source / type</th>
                <th>State</th>
                <th>Hierarchy</th>
                <th>Last updated</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let customer of customerCatalog?.customers">
                <td>
                  <strong>{{customer.displayName}}</strong>
                  <small *ngIf="customer.companyName">Company: {{customer.companyName}}</small>
                </td>
                <td><span class="source-pill">QuickBooks Online / Customer</span></td>
                <td><span class="state-pill compact" [attr.data-active]="customer.active">{{customer.active ? 'Active' : 'Inactive'}}</span></td>
                <td>
                  <span>{{customer.fullyQualifiedName || 'Not provided'}}</span>
                  <small *ngIf="customer.parentRef">Parent: {{customer.parentRef.displayName || customer.parentRef.id}}</small>
                </td>
                <td>{{customer.lastUpdatedAt ? (customer.lastUpdatedAt | date:'medium') : 'Not provided'}}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </mat-card>
    </div>
  </section>
</section>
